// src/pages/profile-settings/doctor/PracticeTeamAppointments.jsx
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  getPracticeTeamMemberIdentity,
  getPracticeTeamUnavailableCapabilityAreas,
} from "./practiceTeamWorkspaceModel.js";
import { usePracticeTeamAccess } from "./practiceTeamMemberAccess.js";
import PracticeTeamWorkspaceHeader from "./PracticeTeamWorkspaceHeader.jsx";

const ICON_HOSPITAL = "/icons/doctor/home_content_media/hospital_Fill.svg";
const ICON_PERSON = "/icons/doctor/home_content_media/personal_injury_Filled.svg";
const ICON_SCHEDULE = "/icons/doctor/home_content_media/schedule.svg";
const ICON_SERVICES = "/icons/doctor/home_content_media/medical_services_filled.svg";
const ICON_DATE = "/icons/doctor/home_content_media/date_range.svg";
const ICON_PILL = "/icons/doctor/home_content_media/pill_Fill.svg";

function DelegatedAppointmentsEmptyCard({ title, body }) {
  return (
    <div
      className="
        mt-1
        mb-6
        w-full
        afrimed-card-radius
        bg-[rgb(var(--clr-surface-cont-highest))]
        shadow-[var(--elev-1)]
        px-6
        py-6
        overflow-hidden
      "
    >
      <div className="flex min-h-[140px] items-center justify-between gap-4">
        <div className="max-w-[26rem]">
          <p className="text-title-medium text-on-surface">
            {title}
          </p>
          <p className="mt-2 text-body-medium text-on-surface-var">
            {body}
          </p>
        </div>
        <div className="hidden shrink-0 pr-1 sm:block">
          <div className="grid grid-cols-2 items-center justify-items-center gap-x-6 gap-y-5">
            <img src={ICON_HOSPITAL} alt="" className="h-8 w-8" draggable="false" />
            <img src={ICON_PERSON} alt="" className="h-12 w-12" draggable="false" />
            <img src={ICON_SCHEDULE} alt="" className="h-8 w-8" draggable="false" />
            <img src={ICON_SERVICES} alt="" className="h-12 w-12" draggable="false" />
            <img src={ICON_DATE} alt="" className="h-8 w-8" draggable="false" />
            <img src={ICON_PILL} alt="" className="h-12 w-12" draggable="false" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PracticeTeamAppointments() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { auth, invite, resolving, allowed } = usePracticeTeamAccess({ requiredSectionId: "appointments" });
  const identity = React.useMemo(() => getPracticeTeamMemberIdentity(invite, auth?.user), [auth?.user, invite]);
  const capabilityAreas = React.useMemo(() => {
    return getPracticeTeamUnavailableCapabilityAreas(invite?.role, {
      exclude: ["assignment"],
    });
  }, [invite?.role]);

  if (resolving) {
    return <RouteFallback label="Loading Practice Team..." />;
  }

  if (!allowed) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--afrimed-shell-background))] text-[rgb(var(--clr-on-surface))]">
      <PracticeTeamWorkspaceHeader section="appointments" invite={invite} identity={identity} />
      <SettingsScreenShell className="[--rail-pad:12px] lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <TopBarCenter
          title={t("settings.practiceTeam.appointments.title", "Appointments")}
          onBack={() => navigate("/practice-team/welcome")}
        />

        <div className="space-y-8">
          <p className="-mt-2 text-body-medium text-on-surface-var">
            {t(
              "settings.practiceTeam.appointments.subtitle",
              "Delegated appointment coordination will appear here."
            )}
          </p>

          <DelegatedAppointmentsEmptyCard
            title={t(
              "settings.practiceTeam.appointments.emptyTitle",
              "Appointment access is not yet enabled."
            )}
            body={t(
              "settings.practiceTeam.appointments.emptyBody",
              "This area will support delegated appointment coordination later. For now, appointment workflows remain locked until production permissions, verification and auditing are complete."
            )}
          />

          <section className="space-y-3">
            <h2 className="text-title-small text-on-surface">
              {t("settings.practiceTeam.appointments.capabilitiesTitle", "Locked and coming capabilities")}
            </h2>
            <p className="max-w-[48rem] text-body-medium text-on-surface-var">
              {t(
                "settings.practiceTeam.appointments.capabilitiesBody",
                "These capabilities are derived from the Practice Team workspace model and remain closed until later appointment workflow slices."
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {capabilityAreas.map((area) => (
                <SaveChip
                  key={area.id}
                  label={area.label}
                  tabIndex={-1}
                  aria-disabled="true"
                  className="pointer-events-none min-h-8 px-3 py-1 text-label-large"
                />
              ))}
            </div>
          </section>
        </div>
      </SettingsScreenShell>
    </div>
  );
}
